<template>
	<div id="prazo-listar">
		<ld-table
			:tableQueryKey="tableQueryKey"
			:getData="getPrazos"
			:columnDefs="columnDefs"
			loadingText="Aguarde. Estamos carregando os prazos"
			:update="atualizar"
			:clearFilters="limparFiltros"
			:canClearFilters="hasFiltrosPorDataFinal"
			:onClearFilters="onClearFilters"
			:hasFooter="true"
            :footerKeyData="footerKeyData"
            :footerData="footerData"
            toggleFooterName="Soma"
            :hasFooterInfo="true"
            footerInfo="As duas últimas linhas - em cinza - desta tabela exibem as somas da respectiva coluna, sendo o valor de cima o total da coluna nesta página e o valor de baixo o total geral de toda a tabela."
		>
			<div class="table-prazos-header flex w-full items-end justify-between">
				<div class="flex justify-start">
					<ld-button
					color="success"
					icon-pack="feather" icon="icon-download"
					class="button-export-prazos md:w-auto h-11"
					@click="exportarExcel"
					>Exportar</ld-button>
				</div>

				<div class="filter-date-container flex items-end justify-end flex-col-reverse md:flex-row lg:flex-no-wrap ml-10">
					<div class="filter-date-content flex items-end md:w-auto">
						<vs-input
							name="filter_de"
							class="w-32 mr-2"
							label="De"
							v-model="filtrosPorDataFinal.dataDe"
							@input="limparFiltroPorDataFinal"
							@input.native="handlerFormatInputData"
						></vs-input>
						<vs-input
							name="filter_ate"
							class="w-32 mr-2"
							label="Até"
							v-model="filtrosPorDataFinal.dataAte"
							@input="limparFiltroPorDataFinal"
							@input.native="handlerFormatInputData"
						></vs-input>
						<ld-button
							type="border"
							class="flex-1 md:w-32 h-11"
							@click="atualizarTabela"
							>Filtrar</ld-button
						>
					</div>
					<ld-button
						v-if="
							$permissions.can('autenticadosuperuser') ||
								$permissions.can('prazoadicionar')
						"
						class="button-add-prazo w-full order-1 md:w-auto h-11 ml-10 mt-4"
						style="min-width: 228px;"
						icon="add"
						@click="adicionarPrazo"
						>Adicionar prazo</ld-button
					>
				</div>
			</div>
		</ld-table>

		<popup-adicionar-prazo
			:active.sync="activePopupAdicionarPrazo"
			:prazoCopy="prazoDuplicate"
			:closed="() => {
				limparFiltrosTabela();
			}"
		/>

		<popup-editar-prazo
			:active.sync="popupEditarPrazo"
			:prazoPk="prazoEditar"
			:closed="atualizarTabela"
			:duplicate="onDuplicatePrazo"
		/>
	</div>
</template>

<script>
import { mapActions } from "vuex";

import avatarLeftIcon from "../../../components/ld-table/utils/avatarLeftIcon.js";
import dateFilter from "../../../components/ld-table/utils/dateFilter.js";
import actionButtons from "../../../components/ld-table/utils/actionButtons.js";
import Prazo, { Situacao } from "../../../models/Prazo";
import DateHelper from "../../../helpers/DateHelper";
import PopupAdicionarPrazo from "../calendario/components/simple_calendar/popup_adicionar_prazo/PopupAdicionarPrazo";
import PopupEditarPrazo from "../calendario/components/simple_calendar/popup_editar_prazo/PopupEditarPrazo";

import DAO from '../../../DAO/DAO';

import moment from "moment";

export default {
	name: "Prazos",
	data() {
		return {
			tableQueryKey: "tablePrazoListar",
			paramsExportExcel: {},
			popupEditarPrazo: false,
			prazoEditar: null,
			prazoDuplicate: null,
			filtrosPorDataFinal: {
				dataDe: null,
				dataAte: null
			},
			activePopupAdicionarPrazo: false,
			atualizar: {
				table: false
			},
			limparFiltros: {
				table: false
			},
			footerKeyData: ["pagina_soma", "total_soma"],
            footerData: null,
			columnDefs: [
				{
					headerName: "Prazo",
					field: "titulo",
					style: {
						"min-width": "150px"
					}
				},
				{
					headerName: "Responsável",
					field: "responsavel",
					cellRenderer: avatarLeftIcon(
						prazo =>
							prazo.responsavel
								? prazo.responsavel.fotoThumb
								: null,
						prazo =>
							prazo.responsavel
								? prazo.responsavel.nome
								: "Sem responsável",
						"icon-user"
					),
					style: {
						"min-width": "150px"
					}
				},
				{
					headerName: "Ação",
					field: "acao",
					style: {
						"min-width": "150px"
					}
				},
				{
					headerName: "Data inicial do prazo",
					field: "dt_inicio",
					customFilter: dateFilter(),
					style: {
						"max-width": "157px",
						"min-width": "157px"
					}
				},
				{
					headerName: "Data final do prazo",
					field: "dt_fim",
					customFilter: dateFilter(),
					style: {
						"max-width": "157px",
						"min-width": "157px"
					}
				},
				{
					headerName: "Ciente",
					field: "ciente",
					cellRenderer: params => {
						const ciente = params.value;
						return ciente ? "Sim" : "Não";
					},
					filter: "select",
					filterSelectData: [
						{ text: "Todos", value: "" },
						{ text: "Sim", value: true },
						{ text: "Não", value: false }
					],
					style: {
						"min-width": "150px",
						"max-width": "123px"
					}
				},
				{
					headerName: "Situação",
					field: "situacao",
					cellRenderer: params => {
						const situacao = params.value;
						return Situacao.getNome(situacao);
					},
					filter: "select",
					filterSelectData: [
						{
							text: "Todos",
							value: ""
						},
						...Situacao.listForSelect()
					],
					style: {
						"min-width": "230px"
					}
				},
				{
					headerName: "Tempo trabalhado",
					field: "contagem_tempo_trabalhado",
					filter: false,
					time: true,
					style: {
						"min-width": "230px"
					}
				},
				{
					headerName: "",
					field: "acaoButtons",
					filterColumnDisabled: true,
					cellRenderer: actionButtons([
						{
							name: "Ver",
							onclick: this.verPrazo
						}
					]),
					filter: false,
					style: {
						width: "70px",
						"max-width": "200px",
						"min-width": "70px"
					}
				}
			]
		};
	},
	components: {
		PopupAdicionarPrazo,
		PopupEditarPrazo
	},
	computed: {
		hasFiltrosPorDataFinal() {
			return this.filtrosPorDataFinal.dataDe && this.filtrosPorDataFinal.dataAte;
		},
		empresaPk() {
			try {
				return this.$route.params.empresaPk;
			} catch (error) {
				return null;
			}
		}
	},
	methods: {
		async getPrazos(params) {
			const data = {
				data: [],
				count: 0
			};

			try {
				this.paramsExportExcel = params;

				const { prazos, totalPrazos, paginaSoma, totalSoma} = await Prazo.getPrazosPorEmpresa(
					this.empresaPk,
					params
				);


				this.footerData = {
                    pagina_soma: paginaSoma,
                    total_soma: totalSoma,
                }

				data.data = prazos.map(prazo => {
					prazo.dt_inicio = DateHelper.convertDate(
						prazo.dataInicio,
						null,
						"DD/MM/YYYY"
					);

					prazo.dt_fim = DateHelper.convertDate(
						prazo.dataFim,
						null,
						"DD/MM/YYYY"
					);
					if (prazo.contagem_tempo_trabalhado != null && prazo.contagem_tempo_trabalhado != undefined) {
						prazo.contagem_tempo_trabalhado = moment.duration({
							milliseconds: prazo.contagem_tempo_trabalhado
						}).format("hh[h]:mm[m]:ss[s]", {trim: false});
					}

					return prazo;
				});

				data.count = totalPrazos;
			} catch (error) {
				console.error(error);
			}

			return data;
		},
		verPrazo(data) {
			const ver = () => {
				this.prazoEditar = data.pk;
				this.popupEditarPrazo = true;
			};

			return ver;
		},
		adicionarPrazo() {
			this.prazoDuplicate = null;
			this.activePopupAdicionarPrazo = true;
		},
		onDuplicatePrazo(prazo) {
			this.prazoDuplicate = prazo;
			this.activePopupAdicionarPrazo = true;
		},
		async exportarExcel() {
			const loadingExportarExcel = this.$vsloading({
				color: "#000000",
				backgroundColor: "#ffffffba",
				opacity: 1,
				width: 44,
				height: 44
			});
			try {

				const params = JSON.parse(JSON.stringify(this.paramsExportExcel));
				params["export"] = "excel";

				delete params["limit"];
				delete params["offset"];

				const exportResponse = await Prazo.exportarExcelPrazos(this.empresaPk, params);

				const currentDate = moment().format("DD_MM_YYYY");
                const filename = `Prazos_${currentDate}`;
                const element = document.createElement('a');

                element.setAttribute('href', window.URL.createObjectURL(exportResponse));
                element.setAttribute('download', filename);

                element.style.display = 'none';
                document.body.appendChild(element);

                element.click();

                element.remove();

			} catch (error) {
				console.error(error);
			} finally {
				setTimeout(loadingExportarExcel.hide, 200);
			}
		},
		limparFiltroPorDataFinal(value) {
			if (!value) {
				this.atualizarTabela();
			}
		},
		handlerFormatInputData(event) {
			DateHelper.dateMask(event);
		},
		atualizarTabela() {
			this.atualizar = {
				table: !this.atualizar.table
			};
		},
		onClearFilters() {
			this.filtrosPorDataFinal = {
				dataDe: null,
				dataAte: null
			};
		},
		limparFiltrosTabela() {
			this.filtrosPorDataFinal = {
				dataDe: null,
				dataAte: null
			};
			this.limparFiltros = {
				table: true
			};
		},
		...mapActions(["setBreadcrumb"])
	},
	watch: {
		filtrosPorDataFinal: {
            handler: function(filtrosPorDataFinal) {
				let queries = new URLSearchParams(window.location.search);
				const lastPath = window.location.pathname.split('/').pop();

                if (filtrosPorDataFinal.dataDe && filtrosPorDataFinal.dataAte) {
					const dataDe = DateHelper.convertDate(
						this.filtrosPorDataFinal.dataDe,
						"DD/MM/YYYY",
						"YYYY-MM-DD"
					);
					const dataAte = DateHelper.convertDate(
						this.filtrosPorDataFinal.dataAte,
						"DD/MM/YYYY",
						"YYYY-MM-DD"
					);

					queries.set("periodo", `${dataDe},${dataAte}`);
				} else {
					queries.delete("periodo");
				}

				if (queries) {
					queries = queries.toString();
					window.history.replaceState(null, null, `${lastPath}?${queries}`);
					DAO.setProperty(this.tableQueryKey, queries);
				}
            },
            deep: true
        },
	},
	created() {
		this.setBreadcrumb({
			title: "Gestão de prazos",
			list: [{ name: "Gestão de prazos", active: true }]
		});
	},
	mounted() {
		let queries = new URLSearchParams(window.location.search);

		if (queries.has('periodo')) {
			const periodo = queries.get('periodo').split(',');
			this.filtrosPorDataFinal = {
				dataDe: DateHelper.convertDate(
					periodo[0],
					"YYYY-MM-DD",
					"DD/MM/YYYY",
				),
				dataAte: DateHelper.convertDate(
					periodo[1],
					"YYYY-MM-DD",
					"DD/MM/YYYY",
				),
			}
		}
	}
};
</script>

<style lang="scss">
	@media screen and (max-width: 940px) {
		.table-prazos-header {
				flex-direction: column;

				& > div {
					width: 100%;
				}
		}

		.button-export-prazos {
			width: 100% !important;
			margin-top: 10px;
		}

		.button-add-prazo {
			width: 100% !important;
		}

		.filter-date-container {
			width: 100% !important;
			flex-direction: column-reverse !important;
			justify-content: start !important;
			align-items: flex-end !important;
			margin-bottom: 10px;
		}

		.filter-date-content {
			margin-top: 45px !important;
		}
	}
</style>
